"use client";

import { Accordion } from "@base-ui/react";
import Link from "next/link";
import { useState } from "react";
import mother from "#/assets/images/mother.png";
import ArrowIcon from "@/components/icons/arrow";
import Container from "@/components/ui/container";
import { cn } from "@/lib/utils";
import type {
  BoardMember,
  Book,
  Consulting as ConsultingType,
} from "@/types/dashboard";
import { BoardMemberCarousel } from "./board-member-carousel";
import { BookCarousel } from "./book-carousel";
import { ConsultingCarousel } from "./consulting-carousel";

type CollapseItem = {
  id: number;
  title: string;
  description: string;
  href: string;
};
const BOARD_ID = 1;
const CONSULTING_ID = 3;
const BOOKS_ID = 2;

const collapseItems: CollapseItem[] = [
  {
    description:
      "Uzmanlarımız, bilimsel ve güncel bilgileri sizin için derliyor. Sorularınıza güvenilir yanıtlar ararken, her adımda size doğru rehberliği sunmak için buradayız.",
    href: "",
    id: BOARD_ID,
    title: "Bilim Kurulu",
  },
  {
    description: "Alanında uzman danışmanlarımız her zaman sizlerle.",
    href: "",
    id: CONSULTING_ID,
    title: "Danışmanlıklar",
  },
  {
    description:
      "Sizler için hazırladığımız cep kitapçıklarımızla her an yanınızdayız.",
    href: "",
    id: BOOKS_ID,
    title: "e-Kitaplar",
  },
];

const CollapsibleItem = ({ item }: { item: CollapseItem }) => {
  return (
    <Accordion.Item
      className="flex flex-col justify-center gap-6"
      value={item.id}
    >
      <Accordion.Trigger
        className={cn(
          "group flex items-center gap-2 cursor-pointer",
          "text-[28px] leading-8.75 font-bold hover:bg-brand-blue-100 p-1 rounded-lg",
          "focus-visible:outline-2 focus-visible:outline-blue-800 active:bg-brand-blue-200",
        )}
      >
        {item.title}
      </Accordion.Trigger>
      <Accordion.Panel
        className={cn(
          "flex [&[hidden]:not([hidden='until-found'])]:hidden",
          "h-(--accordion-panel-height) flex-col justify-end overflow-hidden",
          "text-xl leading-6.5 p-1 font-medium transition-all ease-out  duration-150",
        )}
      >
        <div className="flex flex-col justify-between min-h-56">
          <p className="w-131">{item.description}</p>
          <Link
            className="bg-brand-blue-600 text-white text-base inline-flex items-center gap-1 leading-5 px-6 py-2.5 rounded-full w-fit"
            href={item.href}
          >
            Detaylı Bilgi <ArrowIcon className="stroke-white" />
          </Link>
        </div>
      </Accordion.Panel>
    </Accordion.Item>
  );
};

const Consulting = ({
  boardMembers,
  books,
  consultings,
}: {
  boardMembers: BoardMember[];
  books: Book[];
  consultings: ConsultingType[];
}) => {
  const [activeId, setActiveId] = useState<number>(1);

  return (
    <section>
      <Container className="flex flex-col gap-16">
        <div
          className={cn(
            "w-full h-80 relative rounded-2xl px-6 md:px-16 py-8 md:py-12 md:mb-8 overflow-clip z-1",
            "bg-[#FF6A41] md:bg-linear-to-r from-[#FF6A41] from-40% via-80% via-white to-[#FF6A41] to-100%",
          )}
        >
          <div className="relative z-10 w-44 ml-auto md:ml-0 md:w-full">
            <h1 className="text-2xl leading-7 md:text-[48px] md:leading-14 font-bold text-white">
              Emzirme Danışmanlığı
            </h1>
            <p className="md:text-lg md:leading-4.5 font-medium text-white md:w-104.5 mt-4">
              Emzirme Destek Elçilerimiz size birebir ve ücretsiz danışmanlık
              vermeye hazır!
            </p>
            <Link
              className="bg-white text-brand-blue-600 w-fit mt-9 flex flex-row justify-center items-center gap-2 px-4 py-2 rounded-full hover:bg-brand-blue-100 hover:cursor-pointer"
              href="/emzirme-ve-beslenme"
            >
              Detaylı Bilgi
              <ArrowIcon
                className="stroke-brand-blue-600"
                data-icon="align-end"
              />
            </Link>
          </div>
          <div
            className={cn(
              "absolute z-0 inset-0 bg-no-repeat px-16 py-12",
              "rotate-y-180 md:rotate-y-0 bg-position-[210px] md:bg-position-[98%]",
            )}
            style={{ backgroundImage: `url(${mother.src})` }}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 min-h-96">
          <Accordion.Root
            className="divide-neutral-200"
            multiple={false}
            onValueChange={(vals) => {
              const [firstVal] = vals;
              if (firstVal !== undefined) {
                setActiveId(firstVal as number);
              }
            }}
            value={[activeId]}
          >
            {collapseItems.map((item) => (
              <CollapsibleItem item={item} key={item.id} />
            ))}
          </Accordion.Root>
          {activeId === BOARD_ID && (
            <BoardMemberCarousel members={boardMembers} />
          )}
          {activeId === BOOKS_ID && <BookCarousel books={books} />}
          {activeId === CONSULTING_ID && (
            <ConsultingCarousel consultings={consultings} />
          )}
        </div>
      </Container>
    </section>
  );
};

export default Consulting;
